import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader } from '../ui/Card';
import { Button } from '../ui/Button';
import { MappingSection } from './MappingSection';
import IndustryProfileBulkMapping from './IndustryProfileBulkMapping';
import { loadIndustryProfileMappings } from '../../services/industryProfileAdminService';

const relevance = {
  context: [
    { value: 'general', label: 'Generell' },
    { value: 'elevated', label: 'Forhøyet' },
    { value: 'conditional', label: 'Betinget' },
    { value: 'lower_priority', label: 'Lavere prioritet' }
  ],
  question: [
    { value: 'core', label: 'Kjerne' },
    { value: 'industry_focused', label: 'Bransjefokusert' },
    { value: 'conditional', label: 'Betinget' }
  ]
};

export const IndustryProfileVersionDetail = ({
  version,
  profileKey,
  catalogs,
  busy,
  onMutation,
  onPublish,
  onRetire
}) => {
  const [mappings, setMappings] = useState({ hazards: [], factors: [], questions: [] });
  const [loading, setLoading] = useState(true);
  const [mappingsLoaded, setMappingsLoaded] = useState(false);
  const [mappingError, setMappingError] = useState('');
  const requestIdentity = useRef(0);
  const currentVersionId = useRef(version.id);
  currentVersionId.current = version.id;
  const editable = version.status === 'draft';

  const refreshMappings = async () => {
    const versionId = version.id;
    const requestId = ++requestIdentity.current;

    try {
      const refreshedMappings = await loadIndustryProfileMappings(versionId);
      const isCurrentRequest = requestId === requestIdentity.current &&
        currentVersionId.current === versionId;

      if (isCurrentRequest) {
        setMappings(refreshedMappings);
        setMappingsLoaded(true);
        setMappingError('');
      }
      return isCurrentRequest ? refreshedMappings : null;
    } catch (error) {
      if (requestId === requestIdentity.current && currentVersionId.current === versionId) {
        setMappingError(error.message || 'Profilkoblingene kunne ikke lastes.');
      }
      throw error;
    }
  };

  useEffect(() => {
    let active = true;
    const requestId = ++requestIdentity.current;
    currentVersionId.current = version.id;
    setLoading(true);
    setMappingsLoaded(false);
    setMappingError('');

    loadIndustryProfileMappings(version.id)
      .then((result) => {
        if (active && requestId === requestIdentity.current && currentVersionId.current === version.id) {
          setMappings(result);
          setMappingsLoaded(true);
        }
      })
      .catch((error) => {
        if (active && requestId === requestIdentity.current && currentVersionId.current === version.id) {
          setMappingError(error.message || 'Profilkoblingene kunne ikke lastes.');
        }
      })
      .finally(() => {
        if (active && requestId === requestIdentity.current && currentVersionId.current === version.id) {
          setLoading(false);
        }
      });
    return () => { active = false; };
  }, [version.id]);

  const handleMutation = async (operation, ...args) => {
    await onMutation(operation, ...args);
    await refreshMappings();
  };

  const catalogLabel = (items, id, field) => {
    const item = items.find((entry) => entry.id === id);
    if (!item) return '';
    if (field === 'question') return `${item.question_key || 'Spørsmål'} · ${item.question_text}`;
    return `${item.code || ''} · ${item[field] || ''}`;
  };

  if (loading) return <div className="rounded-2xl border border-slate-200 bg-white p-8 text-sm text-slate-500">Henter profilkoblinger …</div>;

  return (
    <div className="space-y-5">
      <Card>
        <CardHeader
          title={`${profileKey} · ${version.display_name}`}
          subtitle={`Versjon ${version.version_number} · ${statusLabel(version.status)}`}
          action={editable ? (
            <Button type="button" size="sm" onClick={() => onPublish?.()}>Publiser profil</Button>
          ) : version.status === 'published' ? (
            <Button type="button" size="sm" variant="secondary" onClick={onRetire}>Marker som utgått</Button>
          ) : null}
        />
        <CardContent>
          <p className="text-sm leading-6 text-slate-600">{version.description || 'Ingen beskrivelse er angitt.'}</p>
          {!editable && <p className="mt-4 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-800">Denne versjonen er skrivebeskyttet. Bare utkast kan endres.</p>}
        </CardContent>
      </Card>

      {mappingError && (
        <div role="alert" className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          Profilkoblingene kunne ikke lastes eller oppdateres: {mappingError}
        </div>
      )}

      {editable && mappingsLoaded && (
        <>
          <IndustryProfileBulkMapping
            catalogs={catalogs}
            mappings={mappings}
            version={version}
            onApplied={refreshMappings}
          />
          <MappingSection title="Farer" type="hazard" targetField="hazard_id" catalogue={catalogs.hazards} mappings={mappings.hazards} labelFor={(id) => catalogLabel(catalogs.hazards, id, 'title')} relevanceOptions={relevance.context} busy={busy} onAdd={(...args) => handleMutation('add', ...args)} onUpdate={(...args) => handleMutation('update', ...args)} onDelete={(...args) => handleMutation('delete', ...args)} />
          <MappingSection title="Beskyttelsesfaktorer" type="protective_factor" targetField="protective_factor_id" catalogue={catalogs.factors} mappings={mappings.factors} labelFor={(id) => catalogLabel(catalogs.factors, id, 'name')} relevanceOptions={relevance.context} busy={busy} onAdd={(...args) => handleMutation('add', ...args)} onUpdate={(...args) => handleMutation('update', ...args)} onDelete={(...args) => handleMutation('delete', ...args)} />
          <MappingSection title="Kartleggingsspørsmål" type="question" targetField="question_id" catalogue={catalogs.questions} mappings={mappings.questions} labelFor={(id) => catalogLabel(catalogs.questions, id, 'question')} relevanceOptions={relevance.question} busy={busy} onAdd={(...args) => handleMutation('add', ...args)} onUpdate={(...args) => handleMutation('update', ...args)} onDelete={(...args) => handleMutation('delete', ...args)} />
        </>
      )}
      {!editable && mappingsLoaded && <MappingReadOnly mappings={mappings} catalogs={catalogs} label={catalogLabel} />}
    </div>
  );
};

const MappingReadOnly = ({ mappings, catalogs, label }) => {
  const sections = [
    ['Farer', mappings.hazards, catalogs.hazards, 'hazard_id', 'title'],
    ['Beskyttelsesfaktorer', mappings.factors, catalogs.factors, 'protective_factor_id', 'name'],
    ['Kartleggingsspørsmål', mappings.questions, catalogs.questions, 'question_id', 'question']
  ];
  return sections.map(([title, entries, catalogue, field, labelField]) => (
    <Card key={title}>
      <CardHeader title={title} subtitle={`${entries.length} koblinger`} />
      <CardContent className="space-y-3">
        {entries.length === 0 ? <p className="text-sm text-slate-500">Ingen koblinger.</p> : entries.map((entry) => (
          <div key={entry.id} className="rounded-xl border border-slate-100 p-4">
            <p className="font-semibold text-slate-800">{label(catalogue, entry[field], labelField)}</p>
            <p className="mt-1 text-xs text-blue-700">{entry.relevance_class}</p>
            <p className="mt-2 text-sm text-slate-600">{entry.rationale}</p>
          </div>
        ))}
      </CardContent>
    </Card>
  ));
};

export const statusLabel = (status) => ({
  draft: 'Utkast',
  published: 'Publisert',
  retired: 'Utgått'
}[status] || status);